import { useEffect, useId, useState } from "react";
import { usePathname } from "@web-ui/adapters/navigation";
import Icon from "@web-ui/components/Icon";
import NavLink from "@web-ui/components/NavLink";

/*
 * A collapsible group of sidebar items. The list opens and closes on a CSS grid row (motion.css
 * .motion-collapse, 0fr to 1fr at --motion-base) instead of react-animate-height (owner 2026-10-13):
 * no measuring, reduced motion wins, and the collapsed items are inert so they take no tab stop. The
 * template's "new product" shortcut, which read its mock routes, is gone.
 */
type DropdownProps = {
    value: {
        title: string;
        icon: string;
        href?: string;
        open?: boolean;
        list?: {
            title: string;
            href: string;
            counter?: number;
        }[];
    };
};

const Dropdown = ({ value }: DropdownProps) => {
    const pathname = usePathname();
    const isActive = value.list?.some((item) => pathname.includes(item.href));
    const [open, setOpen] = useState(Boolean(value.open ?? isActive));
    const listId = useId();
    // A page of the group became current (a link from elsewhere): open it.
    useEffect(() => {
        if (isActive) setOpen(true);
    }, [isActive]);
    // The app decides (NavigationItem.open): follow it whenever it changes.
    useEffect(() => {
        if (value.open !== undefined) setOpen(value.open || Boolean(isActive));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value.open]);

    return (
        <div className="relative">
            <button
                type="button"
                className={`group relative flex items-center gap-3 w-full h-12 px-3 text-button transition-colors hover:text-t-primary ${
                    open ? "text-t-primary" : "text-t-secondary"
                }`}
                aria-expanded={open}
                aria-controls={listId}
                onClick={() => setOpen(!open)}
            >
                <Icon
                    className={`relative z-2 transition-colors group-hover:fill-t-primary ${
                        open ? "fill-t-primary" : "fill-t-secondary"
                    }`}
                    name={value.icon}
                />
                <div className="relative z-2">{value.title}</div>
                <Icon
                    className={`relative z-2 ms-auto transition-all group-hover:fill-t-primary ${
                        open ? "fill-t-primary rotate-180" : "fill-t-secondary"
                    }`}
                    name="chevron"
                />
            </button>
            <div className="motion-collapse" data-open={open} id={listId}>
                <div inert={!open}>
                    <div className="relative flex flex-col ps-9 before:absolute before:top-0 before:start-[1.4375rem] before:bottom-12 before:w-[1.5px] before:bg-s-stroke2">
                        {value.list?.map((item) => (
                            <div className="relative" key={item.title}>
                                <div className="absolute top-0 -start-[0.8125rem] bottom-[calc(50%-0.75px)] w-[0.8125rem] border-s border-b border-s-stroke2 rounded-es-[10px]"></div>
                                <NavLink value={item} onClick={() => setOpen(true)} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dropdown;
